import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { parseAppVersion, runningAppVersion } from "@/lib/appVersion";
import { reloadOnce } from "@/lib/selfHeal";
import { createVersionTracker, fetchVersionInfo } from "@/lib/versionCheck";

/** How often an open tab asks `/version.json` whether a newer release is out. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** A tab switched back to within this long after a check does not check again. */
export const VISIBLE_CHECK_MIN_GAP_MS = 60 * 1000;

/** Coming back after at least this long hidden counts as a fresh start: reload. */
export const LONG_HIDDEN_MS = 30 * 60 * 1000;

/** `sessionStorage` guard so a `min_version` reload happens once per window. */
export const MIN_VERSION_RELOAD_KEY = "bondit:min-version-reload";

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

const isEditable = (element: Element | null): boolean => {
  if (!element) {
    return false;
  }
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    return true;
  }
  if (element instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(element.type);
  }
  return element instanceof HTMLElement && element.isContentEditable;
};

/** A modal is open: an automatic reload would throw away what is in it. */
export const isDialogOpen = (doc: Document = document): boolean =>
  doc.querySelector('[role="dialog"], [role="alertdialog"]') !== null;

/**
 * The user may be mid-task: a dialog is open or a text field has focus. Used
 * when a tab comes back from the background, where focus still reflects what
 * the user was doing. (Not on a page change: the new page may autofocus a
 * field the user has not touched.)
 */
export const isUserBusy = (doc: Document = document): boolean =>
  isDialogOpen(doc) || isEditable(doc.activeElement);

const reloadPage = () => window.location.reload();

interface NewVersionCheckOptions {
  /** Defaults to the build's `VITE_APP_VERSION`. `dev` or unparsable disables the check. */
  runningVersion?: string;
  intervalMs?: number;
}

export interface NewVersionCheck {
  /** The newer release, once seen on two consecutive checks; else null. */
  newVersion: string | null;
  reload: () => void;
}

/**
 * Notices a newer deployed release and gets the tab onto it (#917).
 *
 * Polls `/version.json` every {@link VERSION_CHECK_INTERVAL_MS} and when the
 * tab becomes visible. Once a newer release is confirmed (see
 * `createVersionTracker`), it reloads by itself on the next page change
 * unless a dialog is open, or when the tab returns after {@link LONG_HIDDEN_MS}
 * hidden unless the user is busy ({@link isUserBusy}). A `min_version` above
 * the running release reloads immediately, once per guard window. Network
 * errors are ignored. Mount it once, inside the router.
 */
export const useNewVersionCheck = ({
  runningVersion = runningAppVersion(),
  intervalMs = VERSION_CHECK_INTERVAL_MS,
}: NewVersionCheckOptions = {}): NewVersionCheck => {
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const enabled = parseAppVersion(runningVersion) !== null;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const tracker = createVersionTracker(runningVersion);
    let disposed = false;
    let confirmed = false;
    // The page was just loaded: no need to re-check on an immediate tab flip.
    let lastCheckAt = Date.now();
    let hiddenAt: number | null = document.visibilityState === "hidden" ? Date.now() : null;

    const check = async () => {
      lastCheckAt = Date.now();
      const info = await fetchVersionInfo();
      if (disposed || info === null) {
        return;
      }
      const observation = tracker.observe(info);
      if (observation.reloadRequired && reloadOnce(MIN_VERSION_RELOAD_KEY)) {
        return;
      }
      if (observation.newVersion !== null) {
        confirmed = true;
        setNewVersion(observation.newVersion);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const hiddenFor = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      const due = Date.now() - lastCheckAt >= VISIBLE_CHECK_MIN_GAP_MS;
      void (due ? check() : Promise.resolve()).then(() => {
        if (!disposed && confirmed && hiddenFor >= LONG_HIDDEN_MS && !isUserBusy()) {
          reloadPage();
        }
      });
    };

    const timer = setInterval(() => void check(), intervalMs);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, runningVersion, intervalMs]);

  // The next page change after a confirmed release reloads onto it. Only the
  // pathname counts: query-string updates (filters, week navigation) are not
  // a page change.
  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPathname.current) {
      return;
    }
    lastPathname.current = pathname;
    if (newVersion !== null && !isDialogOpen()) {
      reloadPage();
    }
  }, [pathname, newVersion]);

  return { newVersion, reload: reloadPage };
};
